import React, { useEffect, useState } from "react";
import axios from "axios";
import { Badge, Button, Card, Modal, Nav } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAward, faListCheck, faTrash, faTrophy } from "@fortawesome/free-solid-svg-icons";
import { ShowConfirm } from "./Alerts.js";
import { PLATFORM02_ACHIEVEMENT_GROUPS, PLATFORM02_SCORE_GROUPS } from "../utils/platform02GuideGamification.js";

const formatScoreDate = (value) => {
  if (!value) return "Data não informada";
  const normalized = typeof value === "string" && /^\d{4}-\d{2}-\d{2}\s/.test(value)
    ? value.replace(" ", "T")
    : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("pt-BR", { dateStyle: "short" }) + " " + date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatScoreValue = (value) => Number(value || 0).toLocaleString("pt-BR");

const formatScoreMultiplier = (value) => {
  const multiplier = Number(value);
  if (!Number.isFinite(multiplier) || multiplier <= 1) return null;
  return `${multiplier.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}×`;
};

const SCORE_PRESENTATION = Object.freeze(PLATFORM02_SCORE_GROUPS.flatMap((group) => (
  group.items.map((rule) => [rule.type, {
    name: rule.title,
    type: group.title,
    detail: rule.logic,
  }])
)).reduce((result, [type, presentation]) => {
  result[type] = presentation;
  return result;
}, {}));

const getScorePresentation = (score) => {
  const reliable = SCORE_PRESENTATION[score?.type] || {};
  const isActivity = String(score?.type || "").startsWith("activity_");
  const isDerivedQuiz = score?.type === "activity_quiz_completed" && !score?.activity_title;
  const activityTitle = score?.activity_title || (isDerivedQuiz ? score?.description : null);
  const name = reliable.name || score?.activity_title || score?.description || score?.type || "Pontuação";
  const configuredDescription = isActivity
    && !isDerivedQuiz
    && score?.description
    && score.description !== reliable.detail
    ? score.description
    : null;

  return {
    activityTitle,
    configuredDescription,
    description: reliable.detail || score?.description,
    isActivity,
    name,
    type: reliable.type || (isActivity ? "Atividade" : "Pontuação"),
  };
};

const normalizeAchievementPageData = (value, participant = null) => {
  if (value && Array.isArray(value.achievements)) return value;
  const completed = Number(participant?.achievements_completed || 0);
  const total = Number(participant?.achievements_total || 0);
  return {
    summary: {
      total,
      completed,
      percent: total > 0 ? Math.round((completed / total) * 100) : 0,
    },
    achievements: [],
  };
};

const getAchievementSections = (value) => {
  const achievements = Array.isArray(value?.achievements) ? value.achievements : [];
  return {
    completed: achievements
      .filter((item) => item.completed)
      .sort((left, right) => new Date(right.completed_at || 0) - new Date(left.completed_at || 0)),
    pending: achievements
      .filter((item) => !item.completed)
      .sort((left, right) => Number(left.sort_order || 0) - Number(right.sort_order || 0)),
  };
};

const getAchievementDefinition = (achievement) => {
  const catalogItem = PLATFORM02_ACHIEVEMENT_GROUPS
    .flatMap((group) => group.items)
    .find((item) => item.code === achievement?.code);
  return {
    description: achievement?.description || catalogItem?.logic || "Conclua esta ação durante o evento.",
    target: Math.max(1, Number(achievement?.target_value || 1)),
  };
};

export const ParticipantScoreHistorySummary = ({ history = null }) => (
  history ? (
    <Card className="mb-3" aria-label="Resumo da pontuação">
      <Card.Body className="p-3">
        <div className="participantScoreSummaryGrid">
          <div className="participantScoreSummaryMetric">
            <small className="d-block text-muted">Posição</small>
            <strong><FontAwesomeIcon icon={faTrophy} className="me-1" />{history.rank_position ? `${history.rank_position}º` : "—"}</strong>
          </div>
          <div className="participantScoreSummaryMetric">
            <small className="d-block text-muted">Total atual</small>
            <strong><FontAwesomeIcon icon={faAward} className="me-1" />{formatScoreValue(history.total_value)}</strong>
          </div>
        </div>
      </Card.Body>
    </Card>
  ) : null
);

export const ParticipantScoreHistoryBasicInfo = ({ participant, emailLabel = "E-mail" }) => {
  if (!participant) return null;
  return (
    <div className="mb-3 participantScoreBasicInfo">
      <p className="fw-bold">Informações Básicas</p>
      <p className="mb-2">
        {emailLabel}
        <br />
        <span className="opacity-75 small text-break">{participant.email || "(Não informado)"}</span>
      </p>
      <p className="mb-2">
        Status
        <br />
        <span className="opacity-75 small">{Number(participant.status) === 0 ? "Inativo" : "Ativo"}</span>
      </p>
    </div>
  );
};

export const ParticipantAchievementProgressSummary = ({ pageData, participant }) => {
  const normalized = normalizeAchievementPageData(pageData, participant);
  const { completed, pending } = getAchievementSections(normalized);
  const total = Number(normalized.summary?.total ?? completed.length + pending.length);
  const completedCount = Number(normalized.summary?.completed ?? completed.length);
  const percent = total > 0 ? Math.round((completedCount / total) * 100) : 0;

  return (
    <Card className="mb-3" aria-label="Progresso das conquistas">
      <Card.Body className="p-3">
        <div className="participantAchievementSummaryHeader">
          <div>
            <small className="d-block text-muted">Conquistas desbloqueadas</small>
            <strong>{completedCount} de {total}</strong>
          </div>
          <strong className="participantAchievementSummaryPercent">{percent}%</strong>
        </div>
        <div
          className="participantAchievementSummaryTrack"
          role="progressbar"
          aria-label="Progresso das conquistas"
          aria-valuemin="0"
          aria-valuemax={total || 1}
          aria-valuenow={completedCount}
        >
          <span style={{ width: `${percent}%` }} />
        </div>
        <small className="d-block text-muted mt-2">
          {total === 0
            ? "Nenhuma conquista disponível."
            : pending.length > 0 || completedCount < total
              ? `${Math.max(0, total - completedCount)} conquista${total - completedCount === 1 ? "" : "s"} restante${total - completedCount === 1 ? "" : "s"}.`
              : "Todas as conquistas foram desbloqueadas."}
        </small>
      </Card.Body>
    </Card>
  );
};

const AchievementCard = ({ achievement }) => {
  const definition = getAchievementDefinition(achievement);
  const progress = Math.min(Math.max(0, Number(achievement.progress_value || 0)), definition.target);
  const percent = Math.min(100, Math.round((progress / definition.target) * 100));
  const completedDate = achievement.completed_at ? new Date(achievement.completed_at) : null;
  const completedAt = completedDate && !Number.isNaN(completedDate.getTime())
    ? `${completedDate.toLocaleDateString("pt-BR", { dateStyle: "medium" })} ${completedDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
    : null;

  return (
    <article aria-label={achievement.title} className={`achievementCardV2 card ${achievement.completed ? "achievementCardCompleted" : "achievementCardPending"}`}>
      <div className="card-body">
        <div className="achievementCardMedia" aria-hidden="true">
          <div className="achievementCardIcon"><FontAwesomeIcon icon={faAward} /></div>
        </div>
        <div className="achievementCardContent">
          <div className="achievementCardTitleRow">
            <h6>{achievement.title}</h6>
            <Badge bg={achievement.completed ? "primary" : "secondary"} text={achievement.completed ? "dark" : undefined} className="text-nowrap">
              {achievement.completed ? "Desbloqueada" : "Em andamento"}
            </Badge>
          </div>
          <p>{definition.description}</p>
          <div className="achievementProgressMeta">
            <span>Progresso</span>
            <strong>{progress}/{definition.target}</strong>
          </div>
          <div className="achievementProgressTrack" role="progressbar" aria-valuemin="0" aria-valuemax={definition.target} aria-valuenow={progress}>
            <span style={{ width: `${percent}%` }} />
          </div>
          {completedAt && <small>{completedAt}</small>}
        </div>
      </div>
    </article>
  );
};

export const AchievementSections = ({ pageData, participant }) => {
  const normalized = normalizeAchievementPageData(pageData, participant);
  const { completed, pending } = getAchievementSections(normalized);
  if (!completed.length && !pending.length) {
    return <p className="text-center text-muted my-4">Detalhes das conquistas ainda não estão disponíveis.</p>;
  }

  return (
    <div className="achievementGroups pt-1">
      {[
        ["Conquistas desbloqueadas", completed, true],
        ["Próximas conquistas", pending, false],
      ].map(([title, achievements, isCompleted]) => (
        <section className="achievementGroup" aria-label={title} key={title}>
          <div className="achievementGroupHeader"><h5>{title}</h5><span>{achievements.length}</span></div>
          {achievements.length > 0
            ? <div className="achievementCardGrid">{achievements.map((achievement) => <AchievementCard key={achievement.code || achievement.id} achievement={achievement} />)}</div>
            : <div className="achievementGroupEmpty">{isCompleted ? "Suas conquistas desbloqueadas aparecerão aqui." : "Nenhuma conquista pendente."}</div>}
        </section>
      ))}
    </div>
  );
};

const ParticipantScoreHistoryModal = ({
  dataTable,
  canEdit = false,
  onHide,
  participant,
  onChanged,
  show,
}) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [deletingKey, setDeletingKey] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [activeTab, setActiveTab] = useState("score");

  useEffect(() => {
    if (!show || !participant?.user_id || !dataTable?.event_id) return undefined;
    let active = true;
    setHistory(null);
    setError("");
    setDeleteError("");
    setActiveTab("score");
    setLoading(true);
    axios.get("/api/event/plataformaEventos/participant-score-history", {
      params: {
        event_id: dataTable.event_id,
        user_id: participant.user_id,
      },
    }).then((response) => {
      if (active) setHistory(response.data);
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.message || "Não foi possível carregar a pontuação.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [dataTable?.event_id, participant?.user_id, reload, show]);

  const scores = Array.isArray(history?.scores) ? history.scores : [];

  const deleteScore = async (score) => {
    const key = [score.type, score.score_id, score.identifier].join("-");
    setDeletingKey(key);
    setDeleteError("");
    try {
      await axios.delete("/api/event/plataformaEventos/participant-score-history", {
        params: {
          event_id: dataTable.event_id,
          user_id: participant.user_id,
          score_id: score.score_id ?? undefined,
          type: score.type,
          identifier: score.identifier,
          scope: "single",
        },
      });
      setReload((value) => value + 1);
      onChanged?.();
    } catch (requestError) {
      setDeleteError(requestError.response?.data?.message || "Não foi possível excluir esta pontuação.");
    } finally {
      setDeletingKey("");
    }
  };

  const confirmScoreDeletion = (score) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose: closeConfirm }) => (
        <ShowConfirm
          title="Confirmar ação"
          description="Deseja excluir somente este registro de pontuação? A conquista relacionada, quando existir, será mantida."
          action={() => {
            closeConfirm();
            deleteScore(score);
          }}
          onClose={closeConfirm}
        />
      ),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="md" fullscreen="sm-down" className="managerParticipantRankingModal rankingContainer">
      <Modal.Header closeButton>
        <Modal.Title>Pontuação e conquistas</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ParticipantScoreHistoryBasicInfo participant={participant} emailLabel={dataTable?.json?.form?.emailLabel || "E-mail"} />
        <Nav variant="tabs" activeKey={activeTab} onSelect={(nextTab) => nextTab && setActiveTab(nextTab)} className="mb-3">
          <Nav.Item><Nav.Link eventKey="score">Pontuação</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="achievements">Conquistas</Nav.Link></Nav.Item>
        </Nav>
        {activeTab === "score" ? (
          <>
            <ParticipantScoreHistorySummary history={history} />
            {deleteError && <p className="text-danger text-center">{deleteError}</p>}
            {loading ? (
              <p className="text-center opacity-75 my-4">Carregando pontuação...</p>
            ) : error ? (
              <div className="text-center my-4">
                <p className="text-danger">{error}</p>
                <Button variant="outline-primary" size="sm" onClick={() => setReload((value) => value + 1)}>Tentar novamente</Button>
              </div>
            ) : scores.length > 0 ? (
              scores.map((score) => {
                const presentation = getScorePresentation(score);
                const multiplierLabel = formatScoreMultiplier(score.score_multiplier);
                const deleting = deletingKey === [score.type, score.score_id, score.identifier].join("-");
                return (
                  <Card key={[score.type, score.score_id, score.identifier, score.date].join("-")} className="mb-2">
                    <Card.Body className="p-3">
                      <div className="participantScoreCardHeader">
                        <div className="participantScoreCardTitleGroup">
                          {presentation.isActivity && <span className="participantScoreActivityIcon" aria-hidden="true"><FontAwesomeIcon icon={faListCheck} /></span>}
                          <strong className="participantScoreCardTitle">{presentation.name}</strong>
                          <Badge bg="primary" className="participantScoreValueBadge text-nowrap">{formatScoreValue(score.value)} pts</Badge>
                        </div>
                        <div className="participantScoreCardActions">
                          {multiplierLabel && <Badge bg="warning" text="dark" className="text-nowrap">Bônus aplicado: {multiplierLabel}</Badge>}
                          {canEdit && (
                            <Button variant="outline-danger" size="sm" disabled={Boolean(deletingKey)} onClick={() => confirmScoreDeletion(score)} title="Excluir pontuação" aria-label="Excluir pontuação">
                              <FontAwesomeIcon icon={faTrash} spin={deleting} />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="participantScoreCardContent">
                        <Badge bg={presentation.isActivity ? "warning" : "secondary"} text={presentation.isActivity ? "dark" : undefined} className="mt-1">{presentation.type}</Badge>
                        {presentation.isActivity ? (
                          <div className="participantScoreActivityDetails">
                            {presentation.activityTitle && <small className="d-block text-muted mt-2"><strong>Atividade:</strong> {presentation.activityTitle}</small>}
                            {presentation.configuredDescription && <small className="d-block text-muted mt-1"><strong>Descrição:</strong> {presentation.configuredDescription}</small>}
                            {presentation.description && presentation.description !== presentation.configuredDescription && <small className="d-block text-muted mt-1"><strong>Quando pontua:</strong> {presentation.description}</small>}
                          </div>
                        ) : presentation.description ? <small className="d-block text-muted mt-2">{presentation.description}</small> : null}
                      </div>
                      <div className="participantScoreCardMeta"><small className="participantScoreCardDate text-muted text-nowrap">{formatScoreDate(score.date)}</small></div>
                      {score.included_in_total === false && <Badge bg="secondary" className="mt-2">Fora do total do ranking</Badge>}
                    </Card.Body>
                  </Card>
                );
              })
            ) : (
              <p className="text-center opacity-75 my-4">Nenhuma pontuação registrada.</p>
            )}
          </>
        ) : (
          <>
            <ParticipantAchievementProgressSummary pageData={history?.achievements} participant={participant} />
            {loading ? <p className="text-center opacity-75 my-4">Carregando conquistas...</p> : error ? <p className="text-center text-danger my-4">{error}</p> : <AchievementSections pageData={history?.achievements} participant={participant} />}
          </>
        )}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Fechar</Button></Modal.Footer>
    </Modal>
  );
};

export default ParticipantScoreHistoryModal;
